import React from 'react';
import styled from 'styled-components';
import CubeIcon from '@svg/cube';
import TelnyxLogo from '@svg/telnyx';
import DotLoader from '@svg/dot_loader';

import { Stack } from '@mui/material';
import { useTheme } from '../../../styles';
import { Tooltip } from '../..';
import { State } from '@store/index';
import { useSelector } from 'react-redux';

import {
  AutoAwesomeMotionOutlined,
  BuildOutlined,
  CallSplitOutlined,
  CheckCircleOutline,
  CodeOutlined,
  DoubleArrowOutlined,
  HighlightOffOutlined,
  KeyboardTabOutlined,
  PhoneCallbackOutlined,
  PublicOutlined,
  QuestionMarkOutlined,
  SignpostOutlined,
  StartOutlined,
  SaveOutlined,
  AirlineStopsOutlined,
  TimerOutlined,
  AbcOutlined,
  ScheduleRounded,
  SendOutlined,
  RouterOutlined,
} from '@mui/icons-material';

export const NodeHeaderContainer = styled.div<{ background?: string }>`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.text.light};

  font-size: 1.2ch;
  padding: 0.75rem 1rem;
  border-radius: 7px 7px 0 0;
  font-family: 'Inter';

  border-bottom: 1px solid ${({ theme }) => theme.line.node};

  #title {
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.text.standard};
    gap: 12px;
    min-width: 0; // Could also give a overflow: hidden; white-space: nowrap;

    h1 {
      font-weight: 500;
      font-size: 1.7ch;
      margin: 0;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      margin-right: 10px;
    }

    svg {
      font-size: 2ch;
    }
  }
`;

export const NodeIcon = styled.div<{ background?: string; timer?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ background }) => background || 'transparent'};
  color: ${({ theme }) => theme.text.light};
  flex-shrink: 0;

  border-radius: 6px;
  height: 24px;
  width: ${({ timer }) => (timer ? '40px' : '32px')};

  font-size: 1.5ch;
  font-weight: 500;
  font-family: 'Space Grotesk';

  #smallIcon {
    font-size: 14px !important;
  }

  #bigIcon {
    font-size: 17px !important;
  }

  border: ${({ background, theme }) => !background && `1px solid ${theme.line.light}`};

  svg {
    color: ${({ background, theme }) => !background && theme.text.standard};
  }
`;

export const NodeId = styled.div<{ background?: string; timer?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.badge.background};
  color: ${({ theme }) => theme.text.standard};
  border-radius: 6px;
  font-size: 13px;
  border: 1px solid ${({ theme }) => theme.line.light};
  font-weight: 400;
  height: 24px;
  width: ${({ timer }) => (timer ? 'initial' : '28px')};
  min-width: ${({ timer }) => (timer ? '28px' : 'initial')};
  padding: ${({ timer }) => (timer ? '0 4px' : 'initial')};
  line-height: 0.85rem;
  font-family: 'Space Grotesk';
  user-select: none;

  &:hover {
    border: 1px solid ${({ theme }) => theme.line.standard};
  }
`;

interface Props {
  id: string | number;
  title: string;
  icon: string;
  background: string;
  status?: string | undefined;
}

export const NodeHeader: React.FC<Props> = ({ id, title, icon, background, status = 'pending' }) => {
  const theme = useTheme();

  const workflow_id = useSelector((state: State) => state.tabs.active.flow?.flow_id);
  const traces = useSelector((state: State) => state.tabs.active.run.traces) || [];
  const currentTheme = useSelector((state: State) => state.flow.theme);
  const parsedBackground = background === '#000000' && currentTheme === 'dark' ? theme.background.dark : background;

  const renderHeading = () => {
    const headingElement = <h1>{title}</h1>;
    if (title.length > 25) {
      return (
        <Tooltip arrow title={title} placement='top'>
          {headingElement}
        </Tooltip>
      );
    }
    return headingElement;
  };

  const trace = traces.find((trace) => trace.metadata?.node_id === id && trace.metadata?.workflow_id === workflow_id);
  const max_jumps = trace?.metadata?.max_jumps;
  const jump_increment = trace?.metadata?.jump_increment;
  const hasJumpIncrement = Boolean(max_jumps !== undefined && jump_increment !== undefined);

  return (
    <NodeHeaderContainer background={background}>
      <div id='title'>
        <NodeIcon background={parsedBackground}>{getIcon(icon)}</NodeIcon>
        {renderHeading()}
      </div>
      <Stack direction='row' spacing={1}>
        {hasJumpIncrement && (
          <NodeId timer>
            {jump_increment}/{max_jumps}
          </NodeId>
        )}
        <Tooltip
          sx={{ maxWidth: '500px' }}
          title='This identifier differentiates this node from other nodes of the same type. You can use this identifer to reference variables from this node when connecting two data anchors of the same node type to a single destination.'
          placement='top'
          arrow
        >
          <NodeId background={background}>{`#${id}`}</NodeId>
        </Tooltip>
        {status !== 'pending' && status !== 'none' && (
          <NodeId background={background}>
            {status === 'failed' && <HighlightOffOutlined sx={{ fontSize: '15px', color: theme.telnyx.red }} />}
            {status === 'completed' && <CheckCircleOutline sx={{ fontSize: '15px', color: theme.primary }} />}
            {status === 'processing' && <DotLoader color={theme.primary} height={4} />}
          </NodeId>
        )}
      </Stack>
    </NodeHeaderContainer>
  );
};

export const getIcon = (icon?: string) => {
  if (icon === 'run') return <CodeOutlined sx={{ fontSize: '17px !important' }} />;
  if (icon === 'globe') return <PublicOutlined sx={{ fontSize: '17px !important' }} />;
  if (icon === 'switch') return <SignpostOutlined sx={{ fontSize: '19px !important' }} />;
  if (icon === 'branch') return <CallSplitOutlined sx={{ fontSize: '17px !important' }} />;
  if (icon === 'input') return <StartOutlined sx={{ fontSize: '17px !important' }} />;
  if (icon === 'output') return <KeyboardTabOutlined sx={{ fontSize: '17px !important' }} />;
  if (icon === 'condition') return <DoubleArrowOutlined sx={{ fontSize: '17px !important' }} />;
  if (icon === 'tool') return <BuildOutlined sx={{ fontSize: '14px !important' }} />;
  if (icon === 'workflow') return <AutoAwesomeMotionOutlined sx={{ fontSize: '14px !important' }} />;
  if (icon === 'module') return <CubeIcon height={17} />;
  if (icon === 'phone') return <PhoneCallbackOutlined sx={{ fontSize: '14px !important' }} />;
  if (icon === 'telnyx') return <TelnyxLogo height={14} color='#D3FFA6' />;
  if (icon === 'store') return <SaveOutlined sx={{ fontSize: '14px !important' }} />;
  if (icon === 'jump') return <AirlineStopsOutlined sx={{ fontSize: '14px !important' }} />;
  if (icon === 'delay') return <TimerOutlined sx={{ fontSize: '14px !important' }} />;
  if (icon === 'base64_transformer') return <AbcOutlined sx={{ fontSize: '24px !important' }} />;
  if (icon === 'datetime') return <ScheduleRounded sx={{ fontSize: '14px !important' }} />;
  if (icon === 'unknown') return <QuestionMarkOutlined sx={{ fontSize: '14px !important' }} />;
  if (icon === 'sms') return <SendOutlined sx={{ fontSize: '14px !important' }} />;
  if (icon === 'router') return <RouterOutlined sx={{ fontSize: '14px !important' }} />;

  return icon || 'N/A';
};
